import { formatDate } from '@/lib/formatDate';
import React, { memo, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Prescription, PrescriptionComplaint, PrescriptionMedicine } from "@/types/prescription";
import {
  Calendar,
  Edit,
  Copy,
  Thermometer,
  Heart,
  Activity,
  Gauge,
  Printer,
  Weight,
  Paperclip,
  IndianRupee,
} from "lucide-react";
import { isValid, isSameDay, formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import { useFilledCustomFields } from "@/hooks/usePrescriptionLayout";
import type { VisitRecord } from "@/lib/api";
import { billSummary, billText, examSigns, flagTone, readingItems, recordedByLine } from "@/lib/vitals";

/** Header label for visits that aren't an ordinary completed in-person visit */
export function visitLabel(v: Pick<VisitRecord, "type" | "status" | "outcome">): string | null {
  if (v.type === "record_entry") return "Record entry";
  if (v.type === "review") return "Review";
  if (v.type === "teleconsult") return "Teleconsult";
  if (v.status === "auto_closed") return "Not completed";
  if (v.status === "waiting" || v.status === "in_consultation" || v.status === "on_hold") return "In progress";
  if (v.outcome === "admitted") return "Admitted";
  if (v.outcome === "referred") return "Referred";
  return null;
}

interface VisitCardProps {
  prescription: Prescription;
  onEdit?: (prescription: Prescription) => void;
  onView?: (prescription: Prescription) => void;
  onCopy?: (prescription: Prescription) => void;
  isLatest?: boolean;
  className?: string;
  doctorName?: string;
  /** The visit this prescription belongs to: its vitals (triage included) and documents */
  visit?: VisitRecord;
  /** Patient's age in months, for paediatric H/L flags */
  ageMonths?: number | null;
  /** Below the prescription pad: no Latest / In progress / Record entry labels, triage line or bill */
  prescribing?: boolean;
}

function VisitCard({
  prescription,
  onEdit,
  onView,
  onCopy,
  isLatest = false,
  className,
  doctorName,
  visit,
  ageMonths = null,
  prescribing = false,
}: VisitCardProps) {
  // While prescribing, only labels that change how the visit reads (not completed, review, admitted…)
  const label = visit ? visitLabel(visit) : null;
  const shownLabel = prescribing && (label === "In progress" || label === "Record entry") ? null : label;
  const visitReadings = visit?.readings?.length ? readingItems(visit.readings, ageMonths) : [];
  // Credit triage for vitals taken before the doctor saw the patient
  const triage = visit?.readings?.find(r => r.source === "triage");

  // Doctor name — from prescription data or prop, with "Dr." prefix; null if unavailable
  const displayDoctorName = useMemo(() => {
    const name = (prescription.doctor_name || doctorName || "").trim();
    if (!name) return null;
    const lower = name.toLowerCase();
    if (lower.startsWith("dr.") || lower.startsWith("dr ")) return name;
    return `Dr. ${name}`;
  }, [prescription.doctor_name, doctorName]);

  // Date formatting
  const dateInfo = useMemo(() => {
    if (!prescription?.date) return null;
    try {
      const visitDate = new Date(prescription.date);
      if (!isValid(visitDate)) return null;
      // `date` is date-only (UTC midnight); when the Rx was written that same day, measure "ago" from the real time
      const writtenAt = prescription.created_at ? new Date(prescription.created_at) : null;
      const agoFrom = writtenAt && isValid(writtenAt) && isSameDay(writtenAt, visitDate) ? writtenAt : visitDate;
      return {
        formatted: formatDate(visitDate),
        relative:  formatDistanceToNow(agoFrom, { addSuffix: true }),
      };
    } catch {
      return null;
    }
  }, [prescription?.date, prescription?.created_at]);

  // Complaints
  const complaintsInfo = useMemo(() => {
    if (!Array.isArray(prescription.complaints) || prescription.complaints.length === 0) return null;
    const valid = prescription.complaints.filter(
      (c): c is PrescriptionComplaint => Boolean(c?.complaint?.trim())
    );
    return valid.length ? valid : null;
  }, [prescription.complaints]);

  // Medicines
  const medicineInfo = useMemo(() => {
    if (!Array.isArray(prescription.medicines)) return { count: 0, list: [] };
    const valid = prescription.medicines.filter(
      (m): m is PrescriptionMedicine => Boolean(m?.product?.trim())
    );
    return { count: valid.length, list: valid };
  }, [prescription.medicines]);

  // Investigations
  const investigationInfo = useMemo(() => {
    if (!Array.isArray(prescription.investigations)) return { count: 0, list: [] };
    const valid = prescription.investigations.filter(
      (i): i is string => typeof i === "string" && i.trim().length > 0
    );
    return { count: valid.length, list: valid };
  }, [prescription.investigations]);

  const customFields = useFilledCustomFields(prescription.custom_fields);
  const examination = [examSigns(prescription.vitals), prescription.examination?.trim()].filter(Boolean).join("\n");

  // Has enough content to copy
  const hasSubstantialContent = useMemo(() => Boolean(
    prescription.diagnosis?.trim() ||
    medicineInfo.count > 0 ||
    complaintsInfo?.length ||
    prescription.advice?.trim() ||
    investigationInfo.count > 0
  ), [prescription.diagnosis, medicineInfo.count, complaintsInfo, prescription.advice, investigationInfo.count]);

  const formatDosage = (m: PrescriptionMedicine) => m.dosage || "-";
  const formatTiming = (m: PrescriptionMedicine) =>
    m.taken_when ?? (m as { when?: string }).when ?? "-";

  return (
    <Card className={cn(
      "border border-border shadow-sm overflow-hidden bg-card",
      isLatest && "border-l-4 border-l-primary",
      className
    )}>
      <CardContent className="p-0">

        {/* Header */}
        <div className="px-4 py-2 border-b border-border bg-muted/40">
          {/* Wraps on phones: date line, then the actions */}
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">

            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <span className="font-semibold text-foreground text-sm">
                {displayDoctorName ? `${displayDoctorName} · ` : ""}{dateInfo?.formatted ?? "—"}
              </span>
              <span className="text-xs text-muted-foreground hidden sm:inline">
                ({dateInfo?.relative})
              </span>
              {isLatest && !prescribing && (
                <Badge className="bg-primary text-primary-foreground text-xs px-2 py-0.5">
                  Latest
                </Badge>
              )}
              {shownLabel && (
                <span className="text-2xs font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{shownLabel}</span>
              )}
            </div>

            <div className="flex items-center gap-1.5 flex-shrink-0">
              {onCopy && hasSubstantialContent && (
                <Button variant="outline" size="sm" onClick={() => onCopy(prescription)}
                  className="h-7 px-2 gap-1 text-xs">
                  <Copy className="w-3 h-3" /> Copy Rx
                </Button>
              )}
              {onEdit && (
                <Button variant="outline" size="sm" onClick={() => onEdit(prescription)}
                  className="h-7 px-2 gap-1 text-xs">
                  <Edit className="w-3 h-3" /> Edit
                </Button>
              )}
              <Button variant="default" size="sm" onClick={() => onView?.(prescription)}
                className="h-7 px-2 gap-1 text-xs">
                <Printer className="w-3 h-3" /> Print
              </Button>
            </div>
          </div>
        </div>

        {/* Body — clinical order: Complaints → Vitals → Examination → Diagnosis → Rx → Investigations → Advice → Notes → Next Visit */}
        <div className="p-4 space-y-4">

          {/* Complaints */}
          {complaintsInfo && (
            <div>
              <SectionLabel>Chief Complaints</SectionLabel>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {complaintsInfo.map((c, i) => (
                  <span key={i} className="text-xs bg-muted px-2 py-0.5 rounded-full text-foreground">
                    {c.complaint}
                    {c.duration && <span className="text-muted-foreground ml-1">· {c.duration}</span>}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Visit vitals — everything recorded during the visit (triage and prescription), flagged for age */}
          {visitReadings.length > 0 && (() => {
            return (
              <div>
                <SectionLabel>Vitals</SectionLabel>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                  {visitReadings.map(item => (
                    <div key={item.key} className="flex items-center gap-1.5 text-xs">
                      <span className="text-muted-foreground">{item.label}:</span>
                      <span className={cn("font-semibold tabular-nums text-foreground", flagTone(item.flag, item.key))}>
                        {item.text}
                      </span>
                      {item.flag && <span className={cn("text-2xs font-bold", flagTone(item.flag, item.key))}>{item.flag}</span>}
                    </div>
                  ))}
                </div>
                {triage?.recordedBy && !prescribing && (
                  <p className="text-2xs text-muted-foreground mt-1">Triage by {recordedByLine(triage)}</p>
                )}
              </div>
            );
          })()}

          {/* Vitals — only render if at least one meaningful value exists (exclude empty/"None") */}
          {visitReadings.length === 0 && (() => {
            const v = prescription.vitals;
            if (!v) return null;
            const meaningful = (val?: string | number) => { const s = val != null ? String(val).trim() : ""; return s !== "" && s !== "None"; };
            if (!(meaningful(v.bp) || meaningful(v.pulse) || meaningful(v.spo2) || meaningful(v.temp) || meaningful(v.weight))) return null;
            return (
              <div>
                <SectionLabel>Vitals</SectionLabel>
                <div className="flex flex-wrap gap-4 mt-1">
                  {meaningful(v.bp)     && <VitalItem icon={<Gauge      className="w-3.5 h-3.5 text-primary"           />} label="BP"     value={`${v.bp} mmHg`}  />}
                  {meaningful(v.pulse)  && <VitalItem icon={<Heart      className="w-3.5 h-3.5 text-destructive"       />} label="Pulse"  value={`${v.pulse} bpm`} />}
                  {meaningful(v.spo2)   && <VitalItem icon={<Activity   className="w-3.5 h-3.5 text-success"           />} label="SpO2"   value={`${v.spo2}%`}     />}
                  {meaningful(v.temp)   && <VitalItem icon={<Thermometer className="w-3.5 h-3.5 text-warning"          />} label="Temp"   value={`${v.temp}°F`}    />}
                  {meaningful(v.weight) && <VitalItem icon={<Weight     className="w-3.5 h-3.5 text-muted-foreground"  />} label="Weight" value={`${v.weight} kg`} />}
                </div>
              </div>
            );
          })()}

          {/* Examination: general signs, then the findings as written */}
          {examination && (
            <div>
              <SectionLabel>Examination</SectionLabel>
              <p className="text-sm text-foreground mt-1 whitespace-pre-line">{examination}</p>
            </div>
          )}

          {/* Diagnosis */}
          {prescription.diagnosis && (
            <div>
              <SectionLabel>Diagnosis</SectionLabel>
              <p className="text-sm text-foreground mt-1">{prescription.diagnosis}</p>
            </div>
          )}

          {/* Medicines */}
          {medicineInfo.count > 0 && (
            <div>
              <SectionLabel>Rx</SectionLabel>
              {/* Phones: one row per medicine, no sideways scrolling */}
              <ul className="sm:hidden mt-1 rounded-md border border-border/60 divide-y divide-divider text-xs">
                {medicineInfo.list.map((medicine, index) => (
                  <li key={medicine.id || `med-${index}`} className="flex gap-2 px-2 py-1.5">
                    <span className="w-4 flex-shrink-0 text-muted-foreground tabular-nums">{index + 1}</span>
                    <div className="min-w-0">
                      <span className="block font-semibold text-foreground">{medicine.brand_name || medicine.product}</span>
                      {medicine.generic_name && <span className="block text-2xs text-muted-foreground italic">{medicine.generic_name}</span>}
                      <span className="block mt-0.5 text-muted-foreground">
                        {[medicine.dosage && <span key="d" className="font-mono text-foreground">{medicine.dosage}</span>, formatTiming(medicine) !== "-" && formatTiming(medicine), medicine.duration, medicine.quantity && `Qty ${medicine.quantity}`, medicine.notes]
                          .filter(Boolean).map((part, j) => <span key={j}>{j > 0 && " · "}{part}</span>)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="hidden sm:block overflow-x-auto mt-1 rounded-md border border-border/60 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-muted/60">
                    <tr>
                      {["#", "Medicine", "Dosage", "When", "Duration", "Qty", "Notes"].map((h) => (
                        <th key={h} className={cn(
                          "border-b border-border/40 px-2 py-1.5 font-semibold text-muted-foreground",
                          h === "#" || h === "Qty" ? "text-center w-8" : h === "Medicine" ? "text-left" : "text-center"
                        )}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-divider">
                    {medicineInfo.list.map((medicine, index) => (
                      <tr key={medicine.id || `med-${index}`}>
                        <td className="px-2 py-1.5 text-center text-muted-foreground">{index + 1}</td>
                        <td className="px-2 py-1.5 font-semibold text-foreground">
                          {medicine.brand_name || medicine.product}
                          {medicine.generic_name && (
                            <div className="text-2xs text-muted-foreground font-normal italic">{medicine.generic_name}</div>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-center font-mono">{formatDosage(medicine)}</td>
                        <td className="px-2 py-1.5 text-center">{formatTiming(medicine)}</td>
                        <td className="px-2 py-1.5 text-center">{medicine.duration || "-"}</td>
                        <td className="px-2 py-1.5 text-center">{medicine.quantity || "-"}</td>
                        <td className="px-2 py-1.5 text-center text-muted-foreground">{medicine.notes || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Investigations */}
          {investigationInfo.count > 0 && (
            <div>
              <SectionLabel>Tests Requested</SectionLabel>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {investigationInfo.list.map((test, i) => (
                  <span key={i} className="text-xs bg-muted px-2 py-0.5 rounded-full text-foreground">{test}</span>
                ))}
              </div>
            </div>
          )}

          {/* Advice */}
          {prescription.advice && (
            <div>
              <SectionLabel>Advice</SectionLabel>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{prescription.advice}</p>
            </div>
          )}

          {/* Custom fields (Settings → Prescription layout) */}
          {customFields.map((cf) => (
            <div key={cf.key}>
              <SectionLabel>{cf.label}</SectionLabel>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed whitespace-pre-line">{cf.value}</p>
            </div>
          ))}

          {/* Quick Notes */}
          {prescription.quick_notes && (
            <div>
              <SectionLabel>Notes</SectionLabel>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{prescription.quick_notes}</p>
            </div>
          )}

          {/* The visit's bills (shown only to users who may see billing) */}
          {!prescribing && visit?.invoices?.length ? (
            <div>
              <SectionLabel>Bill</SectionLabel>
              {visit.invoices.length > 3 ? (
                <p className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-foreground">
                  <IndianRupee className="w-3 h-3 text-muted-foreground" /> {billSummary(visit.invoices)}
                  <span className="font-normal text-muted-foreground">— see Bills</span>
                </p>
              ) : (
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                {visit.invoices.map(inv => (
                  <span key={inv.id} className="inline-flex items-center gap-1 text-xs">
                    <IndianRupee className="w-3 h-3 text-muted-foreground" />
                    <span className="text-muted-foreground">{inv.invoiceNumber}</span>
                    <span className={cn("font-semibold",
                      inv.cancelled ? "text-muted-foreground line-through"
                        : inv.paymentStatus === "paid" || inv.paid >= inv.total - 0.01 ? "text-success" : "text-warning")}>
                      {billText(inv)}
                    </span>
                  </span>
                ))}
              </div>
              )}
            </div>
          ) : null}

          {/* Documents attached to this visit */}
          {visit?.documents?.length ? (
            <div>
              <SectionLabel>Documents</SectionLabel>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {visit.documents.map(d => (
                  <span key={d.id} className="inline-flex items-center gap-1 text-xs bg-muted px-2 py-0.5 rounded-full text-foreground max-w-[16rem]">
                    <Paperclip className="w-3 h-3 flex-shrink-0 text-muted-foreground" />
                    <span className="truncate">{d.fileName}</span>
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {/* Next Visit */}
          {prescription.next_visit?.date && (
            <div>
              <SectionLabel>Next Visit</SectionLabel>
              <div className="flex items-center gap-2 mt-1 text-sm text-foreground">
                <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                <span>{formatDate(prescription.next_visit.date)}</span>
                {prescription.next_visit.notes && (
                  <span className="text-muted-foreground">· {prescription.next_visit.notes}</span>
                )}
              </div>
            </div>
          )}

        </div>
      </CardContent>
    </Card>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-2xs font-bold text-muted-foreground uppercase tracking-wider">
      {children}
    </h3>
  );
}

function VitalItem({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-1.5">
      {icon}
      <span className="text-muted-foreground text-xs">{label}:</span>
      <span className="font-semibold text-foreground text-xs">{value}</span>
    </div>
  );
}

export default memo(VisitCard);
